import { DesktopOutlined, MobileOutlined } from '@ant-design/icons';
import { Card, Col, Radio, Row, Select, Space, Typography } from 'antd';
import React from 'react';
import { get, useFormContext, useWatch } from 'react-hook-form';
import styled from 'styled-components';

import type { BannerRootData } from '../../schemas/bannerSchema';
import {
  BannerPlatform,
  BannerSegment,
  BannerSlot,
  platformLabels,
  segmentLabels,
  slotLabels,
} from '../../types/bannerManagement';
import { Section } from '../Section';

const { Text } = Typography;

const BlockCard = styled(Card)`
  border: 1px solid #303030;
  border-radius: 10px;
  .ant-card-body {
    padding: 0;
  }
`;

const BlockHeader = styled.div`
  padding: 14px 20px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.88);
  background: rgba(255, 255, 255, 0.04);
  border-bottom: 1px solid #303030;
`;

const BlockBody = styled.div`
  padding: 20px;
`;

const BlockLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
  display: block;
  margin-bottom: 4px;
`;

const ErrorText = styled(Text)`
  color: #ff4d4f;
  font-size: 12px;
  margin-top: 6px;
`;

const PlatformOptionCard = styled(Card)<{ $selected: boolean }>`
  border: 1px solid ${props => (props.$selected ? '#1677ff' : '#3a3a3a')};
  background: ${props =>
    props.$selected ? 'rgba(24, 144, 255, 0.15)' : 'transparent'};
  cursor: pointer;

  .ant-card-body {
    padding: 18px 16px;
  }
`;

const PlatformIconWrapper = styled.span`
  font-size: 28px;
`;

const StyledSelect = styled(Select)`
  width: 100%;

  .ant-select-selector {
    background-color: transparent !important;
  }
`;

const blockSpaceStyle: React.CSSProperties = {
  width: '100%',
};

export const BannerConfigurationStep: React.FC = () => {
  const {
    control,
    formState: { errors },
    setValue,
  } = useFormContext<BannerRootData>();

  const platform = useWatch({
    control,
    name: 'configuration.platform',
  });

  const segment = useWatch({
    control,
    name: 'configuration.segment',
  });

  const slotType = useWatch({
    control,
    name: 'configuration.slotType',
  });

  const segmentOptions = [
    { label: segmentLabels[BannerSegment.Freemium], value: BannerSegment.Freemium },
    { label: segmentLabels[BannerSegment.Subscriber], value: BannerSegment.Subscriber },
  ];

  const slotOptions = [
    { label: slotLabels[BannerSlot.Top], value: BannerSlot.Top },
    { label: slotLabels[BannerSlot.Middle], value: BannerSlot.Middle },
    { label: slotLabels[BannerSlot.Bottom], value: BannerSlot.Bottom },
  ];

  const platformError = get(errors, 'configuration.platform');
  const segmentError = get(errors, 'configuration.segment');
  const slotError = get(errors, 'configuration.slotType');

  return (
    <Space direction='vertical' size={32} style={{ width: '100%' }}>
      <Section
        title='Estrutura do Banner'
        subtitle='Defina as configurações de exibição'
        type='primary'
      >
        <BlockCard>
          <BlockHeader>Selecione o dispositivo</BlockHeader>
          <BlockBody>
            <Space direction='vertical' size={12} style={blockSpaceStyle}>
              <Radio.Group
                style={{ width: '100%' }}
                value={platform || undefined}
                onChange={event =>
                  setValue('configuration.platform', event.target.value, {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                }
              >
                <Row gutter={12}>
                  <Col xs={24} md={12}>
                    <PlatformOptionCard
                      $selected={platform === BannerPlatform.Web}
                      onClick={() =>
                        setValue('configuration.platform', BannerPlatform.Web, {
                          shouldDirty: true,
                          shouldValidate: true,
                        })
                      }
                    >
                      <Space align='center' size={14}>
                        <Radio value={BannerPlatform.Web} />
                        <PlatformIconWrapper>
                          <DesktopOutlined />
                        </PlatformIconWrapper>
                          {platformLabels[BannerPlatform.Web]}
                      </Space>
                    </PlatformOptionCard>
                  </Col>
                  <Col xs={24} md={12}>
                    <PlatformOptionCard
                      $selected={platform === BannerPlatform.Mobile}
                      onClick={() =>
                        setValue('configuration.platform', BannerPlatform.Mobile, {
                          shouldDirty: true,
                          shouldValidate: true,
                        })
                      }
                    >
                      <Space align='center' size={14}>
                        <Radio value={BannerPlatform.Mobile} />
                        <PlatformIconWrapper>
                          <MobileOutlined />
                        </PlatformIconWrapper>
                          {platformLabels[BannerPlatform.Mobile]}
                      </Space>
                    </PlatformOptionCard>
                  </Col>
                </Row>
              </Radio.Group>
              {platformError && (
                <ErrorText>{platformError.message}</ErrorText>
              )}
            </Space>
          </BlockBody>
        </BlockCard>

        <BlockCard>
          <BlockHeader>Selecione o Segmento</BlockHeader>
          <BlockBody>
            <Space direction='vertical' size={12} style={blockSpaceStyle}>
              <BlockLabel>Segmentação</BlockLabel>
              <StyledSelect
                styles={{ popup: { root: { background: '#141414' } } }}
                  value={segment || undefined}
                  onChange={value =>
                    setValue('configuration.segment', value as string, {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                placeholder='Selecione o Segmento'
                options={segmentOptions}
                status={segmentError ? 'error' : undefined}
                size='large'
              />
              {segmentError && (
                <ErrorText>{segmentError.message}</ErrorText>
              )}
            </Space>
          </BlockBody>
        </BlockCard>

        <BlockCard>
          <BlockHeader>Posição</BlockHeader>
          <BlockBody>
            <Space direction='vertical' size={12} style={blockSpaceStyle}>
              <BlockLabel>Posição de exibição</BlockLabel>
              <StyledSelect
                styles={{ popup: { root: { background: '#141414' } } }}
                  value={slotType || undefined}
                  onChange={value =>
                    setValue('configuration.slotType', value as string, {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                placeholder='Selecione a posição'
                options={slotOptions}
                status={slotError ? 'error' : undefined}
                size='large'
              />
              {slotError && (
                <ErrorText>{slotError.message}</ErrorText>
              )}
            </Space>
          </BlockBody>
        </BlockCard>
      </Section>
    </Space>
  );
};
